import { Button, Modal } from 'antd';
import React, { FC } from 'react';
import styled from 'styles/styled-components';
import translations from 'translations';
import type { ReferralAwaitingFirstOrderResponse } from 'types/schema';
import messages from './messages';

interface Props {
  visible: boolean;
  referralAwaitingFirstOrderData?: ReferralAwaitingFirstOrderResponse | null;
  onClose?: () => void;
}

const StyledModal = styled(Modal)`
  &.ant-modal {
    width: calc(100% - 32px) !important;
    max-width: 500px;

    .ant-modal-content {
      padding: 0;
      border-radius: 16px;
      overflow: hidden;
    }

    .ant-modal-body {
      padding: 0;
    }

    .ant-modal-close {
      top: 15px;
      right: 15px;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;

      .ant-modal-close-x {
        width: 32px;
        height: 32px;
        line-height: 32px;
        font-size: 14px;
        color: #4b5563;
      }
    }
  }
`;

const ContentSection = styled.div`
  position: relative;
  padding: 48px 24px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  font-family:
    'Maven Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    'Arial',
    sans-serif;
`;

const MessageGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const Title = styled.h2`
  width: 100%;
  color: #24272e;
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  margin: 0;
  text-align: center;
  @media (max-width: 991px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const Description = styled.p`
  max-width: 380px;
  color: #303644;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
  text-align: center;
`;

const InfoBox = styled.div`
  width: 100%;
  border-radius: 8px;
  background: #f5f5f5;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 991px) {
    padding: 12px;
  }
`;

const InfoRow = styled.div`
  color: #303644;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  @media (max-width: 991px) {
    font-size: 12px;
  }
`;

const InfoLabel = styled.span`
  font-weight: 400;
`;

const InfoValue = styled.span`
  font-weight: 500;
  color: #111827;
`;

const PrimaryButton = styled(Button)`
  min-width: 82px;
  height: 40px;
  border-radius: 8px;
  border: 0;
  background: #43a047;
  color: #ffffff;
  font-family:
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    'Arial',
    sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  padding: 11px 16px;

  &:hover,
  &:focus {
    background: #388e3c;
    border-color: #388e3c;
    color: #ffffff;
  }
`;

const ReferralRewardModal: FC<Props> = ({ visible, referralAwaitingFirstOrderData, onClose }) => {
  const { referrerName, refereeName } = referralAwaitingFirstOrderData || {};

  const handleConfirm = () => {
    if (onClose) {
      onClose();
      return;
    }
  };

  return (
    <StyledModal open={visible} onCancel={onClose} footer={null} centered width={500} destroyOnClose>
      <ContentSection>
        <MessageGroup>
          <Title>{translations(messages.referralRewardTitle)}</Title>
          <Description>{translations(messages.referralRewardDescription)}</Description>
        </MessageGroup>
        <InfoBox>
          <InfoRow>
            <InfoLabel>{translations(messages.referralRewardReferrer)}: </InfoLabel>
            <InfoValue>{referrerName}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{translations(messages.referralRewardReferee)}: </InfoLabel>
            <InfoValue>{refereeName}</InfoValue>
          </InfoRow>
        </InfoBox>
        <PrimaryButton type="primary" onClick={handleConfirm}>
          {translations(messages.referralRewardConfirm)}
        </PrimaryButton>
      </ContentSection>
    </StyledModal>
  );
};

export default ReferralRewardModal;
